import { IconChevronDown, IconChevronUp, IconPlus, IconSearch } from '@tabler/icons-solidjs'
import { createVirtualizer, defaultRangeExtractor } from '@tanstack/solid-virtual'
import {
  createEffect,
  createMemo,
  createSignal,
  For,
  type JSX,
  onMount,
  Show,
  splitProps,
} from 'solid-js'
import { createViewportHeight } from '../lib/create-viewport-height.ts'
import { Button } from './button.tsx'
import { gridRenderEntries, gridRowCount } from './database-grid-rows.ts'

export interface DatabaseGridColumn<Row> {
  id: string
  header: string
  width?: string
  sortable?: boolean
  sortValue?: (row: Row) => string | number
  compare?: (left: Row, right: Row) => number
  cell: (row: Row) => JSX.Element
}

export interface DatabaseGridProps<Row> {
  'aria-label': string
  rows: Row[]
  columns: DatabaseGridColumn<Row>[]
  tableMinWidth?: string
  getRowId: (row: Row) => string
  /**
   * Groups rows under a heading row. Rows are bucketed in the order the sort produced, so
   * grouping composes with sorting instead of overriding it.
   */
  getGroupLabel?: (row: Row) => string
  getSearchText?: (row: Row) => string
  searchValue?: string
  onSearchChange?: (value: string) => void
  sortColumnId?: string
  sortDirection?: 'ascending' | 'descending'
  /** The caller owns ordering across all server pages when this callback is provided. */
  onSortChange?: (columnId: string, direction: 'ascending' | 'descending') => void
  showSearch?: boolean
  viewLabel?: string
  searchPlaceholder?: string
  selectionMode?: 'none' | 'single' | 'multiple'
  selectedRowIds?: string[]
  onSelectedRowIdsChange?: (ids: string[]) => void
  onRowActivate?: (row: Row) => void
  addLabel?: string
  onAddRow?: () => void
  actions?: JSX.Element
  totalRows?: number
  onEndReached?: () => void
  isLoadingMore?: boolean
  /**
   * The rows on screen are about to be replaced, for example while a typed search is still
   * pending. Announced as `aria-busy`, so assistive technology and tests wait for the new rows.
   */
  busy?: boolean
  /**
   * The row a link points at, such as a dashboard deep link. It is scrolled into view once and
   * marked with `aria-current`, so the user lands on the record instead of searching for it.
   */
  targetRowId?: string
}

const collator = new Intl.Collator('uk-UA', { numeric: true, sensitivity: 'base' })
const INTERACTIVE_ROW_TARGET =
  'a, button, input, label, select, textarea, [role="button"], [role="menuitem"]'

const isInteractiveRowTarget = (target: EventTarget | null): boolean =>
  target instanceof Element && Boolean(target.closest(INTERACTIVE_ROW_TARGET))

const formatRecordCount = (count: number): string => {
  const lastTwoDigits = count % 100
  const lastDigit = count % 10
  if (lastTwoDigits >= 11 && lastTwoDigits <= 14) return `${count} записів`
  if (lastDigit === 1) return `${count} запис`
  if (lastDigit >= 2 && lastDigit <= 4) return `${count} записи`
  return `${count} записів`
}

export const DatabaseGrid = <Row,>(props: DatabaseGridProps<Row>) => {
  const [local] = splitProps(props, [
    'aria-label',
    'rows',
    'columns',
    'tableMinWidth',
    'getRowId',
    'getGroupLabel',
    'getSearchText',
    'searchValue',
    'onSearchChange',
    'sortColumnId',
    'sortDirection',
    'onSortChange',
    'showSearch',
    'viewLabel',
    'searchPlaceholder',
    'selectionMode',
    'selectedRowIds',
    'onSelectedRowIdsChange',
    'onRowActivate',
    'addLabel',
    'onAddRow',
    'actions',
    'totalRows',
    'busy',
    'targetRowId',
    'onEndReached',
    'isLoadingMore',
  ])
  const [internalQuery, setInternalQuery] = createSignal('')
  const query = () => local.searchValue ?? internalQuery()
  const [internalSortColumnId, setSortColumnId] = createSignal<string>()
  const [internalSortDirection, setSortDirection] = createSignal<'ascending' | 'descending'>(
    'ascending',
  )
  const sortColumnId = () => local.sortColumnId ?? internalSortColumnId()
  const sortDirection = () => local.sortDirection ?? internalSortDirection()
  const [focusedRowId, setFocusedRowId] = createSignal<string>()
  const [internalSelectedIds, setInternalSelectedIds] = createSignal<string[]>([])
  let scrollElement!: HTMLDivElement
  let footerElement!: HTMLElement
  const availableHeight = createViewportHeight(
    () => scrollElement,
    () => footerElement,
  )

  const selectedIds = () => local.selectedRowIds ?? internalSelectedIds()
  const selectedSet = createMemo(() => new Set(selectedIds()))
  const selectionMode = () => local.selectionMode ?? 'multiple'
  const selectionEnabled = () => selectionMode() !== 'none'

  const updateSelectedIds = (ids: string[]): void => {
    if (local.selectedRowIds === undefined) setInternalSelectedIds(ids)
    local.onSelectedRowIdsChange?.(ids)
  }

  const visibleRows = createMemo(() => {
    const normalizedQuery = query().trim().toLocaleLowerCase('uk-UA')
    const filteredRows =
      normalizedQuery && local.getSearchText && !local.onSearchChange
        ? local.rows.filter((row) =>
            local.getSearchText?.(row).toLocaleLowerCase('uk-UA').includes(normalizedQuery),
          )
        : local.rows

    if (local.onSortChange || local.onEndReached) return filteredRows
    const column = local.columns.find((candidate) => candidate.id === sortColumnId())
    if (!column?.sortValue && !column?.compare) return filteredRows

    const direction = sortDirection() === 'ascending' ? 1 : -1
    return [...filteredRows].sort((leftRow, rightRow) => {
      if (column.compare) return column.compare(leftRow, rightRow) * direction
      const left = column.sortValue?.(leftRow) ?? ''
      const right = column.sortValue?.(rightRow) ?? ''
      if (typeof left === 'number' && typeof right === 'number') return (left - right) * direction
      return collator.compare(String(left), String(right)) * direction
    })
  })
  const renderEntries = createMemo(() =>
    gridRenderEntries(visibleRows(), local.getRowId, local.getGroupLabel),
  )
  const rangeExtractor = createMemo(() => {
    const focusedIndex = renderEntries().findIndex(
      (entry) => entry.kind === 'row' && entry.key === focusedRowId(),
    )
    return (range: Parameters<typeof defaultRangeExtractor>[0]) => {
      const indexes = defaultRangeExtractor(range)
      if (focusedIndex >= 0 && !indexes.includes(focusedIndex)) indexes.push(focusedIndex)
      return indexes.sort((left, right) => left - right)
    }
  })
  const rowVirtualizer = createVirtualizer<HTMLDivElement, HTMLTableRowElement>({
    get count() {
      return renderEntries().length
    },
    getScrollElement: () => scrollElement ?? null,
    estimateSize: (index) => (renderEntries()[index]?.kind === 'group' ? 30 : 58),
    /* A new function per row order. The virtualizer caches keys until this function changes, so a
       fixed one kept the old order after sorting, and a later refetch rendered one row twice. */
    get getItemKey() {
      const entries = renderEntries()
      return (index: number) => entries[index]?.key ?? index
    },
    get rangeExtractor() {
      return rangeExtractor()
    },
    overscan: 8,
  })
  let scrolledToTarget: string | undefined
  createEffect(() => {
    const target = local.targetRowId
    if (!target || target === scrolledToTarget) return
    const index = renderEntries().findIndex((entry) => entry.kind === 'row' && entry.key === target)
    if (index < 0) return
    scrolledToTarget = target
    rowVirtualizer.scrollToIndex(index, { align: 'center' })
    // The page may need to scroll too when the grid itself is below the fold.
    requestAnimationFrame(() => {
      scrollElement?.querySelector('tr[data-target="true"]')?.scrollIntoView({
        block: 'center',
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      })
    })
  })
  const virtualRows = () => rowVirtualizer.getVirtualItems()
  const bottomSpacer = () => {
    const last = virtualRows().at(-1)
    return last ? Math.max(0, rowVirtualizer.getTotalSize() - last.end) : 0
  }
  const rowCount = () =>
    gridRowCount({
      loadedRows: visibleRows().length,
      renderedRows: renderEntries().length,
      totalRows: local.totalRows,
      grouped: Boolean(local.getGroupLabel),
      canLoadMore: Boolean(local.onEndReached),
    })

  createEffect(() => {
    if (selectionMode() !== 'single') return
    const selected = selectedIds()
    if (selected.length === 0) return
    const visibleIds = new Set(visibleRows().map((row) => local.getRowId(row)))
    const next = selected.filter((rowId) => visibleIds.has(rowId))
    if (next.length !== selected.length) updateSelectedIds(next)
  })

  const allVisibleSelected = () =>
    visibleRows().length > 0 && visibleRows().every((row) => selectedSet().has(local.getRowId(row)))

  const toggleRow = (rowId: string): void => {
    if (selectionMode() === 'single') {
      updateSelectedIds(selectedSet().has(rowId) ? [] : [rowId])
      return
    }
    const next = new Set(selectedIds())
    if (next.has(rowId)) next.delete(rowId)
    else next.add(rowId)
    updateSelectedIds([...next])
  }

  const toggleAllVisible = (): void => {
    const next = new Set(selectedIds())
    if (allVisibleSelected()) {
      for (const row of visibleRows()) next.delete(local.getRowId(row))
    } else {
      for (const row of visibleRows()) next.add(local.getRowId(row))
    }
    updateSelectedIds([...next])
  }

  const canSort = (column: DatabaseGridColumn<Row>) =>
    column.sortable &&
    (local.onSortChange || (!local.onEndReached && (column.sortValue || column.compare)))
  const sortBy = (column: DatabaseGridColumn<Row>): void => {
    if (!canSort(column)) return
    const direction =
      sortColumnId() === column.id && sortDirection() === 'ascending' ? 'descending' : 'ascending'
    if (local.onSortChange) local.onSortChange(column.id, direction)
    else {
      setSortColumnId(column.id)
      setSortDirection(direction)
    }
    scrollElement?.scrollTo({ top: 0 })
  }

  const updateQuery = (value: string): void => {
    if (local.searchValue === undefined) setInternalQuery(value)
    local.onSearchChange?.(value)
  }

  return (
    <section class="min-w-0 max-w-full overflow-hidden rounded-card border border-border bg-surface">
      <header class="flex flex-wrap items-center gap-2 border-b border-border bg-surface-muted px-2 py-2">
        <div class="flex h-8 min-w-0 items-center gap-2 px-1">
          <span class="grid size-6 place-items-center rounded-control bg-ink text-micro font-semibold text-text-inverse">
            DB
          </span>
          <span class="truncate text-body font-semibold text-text">
            {local.viewLabel ?? 'Записи'}
          </span>
        </div>

        <Show when={local.showSearch !== false}>
          <label class="relative min-w-40 flex-1 sm:max-w-72">
            <span class="sr-only">Пошук у таблиці</span>
            <IconSearch
              size={16}
              strokeWidth={1.5}
              class="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-text-subtle"
              aria-hidden="true"
            />
            <input
              class="h-8 w-full rounded-control border max-md:h-(--size-touch) border-border bg-surface pr-3 pl-8 text-body text-text outline-none placeholder:text-text-subtle focus:border-accent focus:ring-2 focus:ring-accent/18"
              value={query()}
              placeholder={local.searchPlaceholder ?? 'Пошук записів…'}
              onInput={(event) => updateQuery(event.currentTarget.value)}
            />
          </label>
        </Show>

        <Show when={local.actions}>
          {(actions) => <div class="flex flex-wrap items-center gap-1">{actions()}</div>}
        </Show>
        <Show when={local.onAddRow}>
          <Button size="sm" onClick={() => local.onAddRow?.()}>
            <IconPlus size={16} strokeWidth={1.5} aria-hidden="true" />
            {local.addLabel ?? 'Додати запис'}
          </Button>
        </Show>
      </header>

      <div
        ref={scrollElement}
        class="w-full max-w-full overflow-auto"
        style={{ 'max-height': `max(180px, calc(${availableHeight()}px - var(--spacing-gutter)))` }}
        onScroll={(event) => {
          if (!local.onEndReached || local.isLoadingMore) return
          const target = event.currentTarget
          if (target.scrollHeight - target.scrollTop - target.clientHeight < 320) {
            local.onEndReached()
          }
        }}
      >
        <table
          class="w-full border-separate border-spacing-0 text-left text-body text-text"
          style={{ 'min-width': local.tableMinWidth ?? '52rem' }}
          aria-label={local['aria-label']}
          aria-rowcount={rowCount()}
          aria-busy={local.busy || undefined}
        >
          <thead>
            <tr aria-rowindex={1}>
              <Show when={selectionEnabled()}>
                <th class="sticky top-0 left-0 z-3 w-10 border-r border-b border-border bg-surface-sunken px-2 py-2 max-md:p-0">
                  <Show when={selectionMode() === 'multiple'}>
                    {/* The label is the 44px tap target on a phone; the box itself stays small. */}
                    <label class="grid place-items-center max-md:size-(--size-touch)">
                      <input
                        type="checkbox"
                        class="size-4 accent-accent"
                        checked={allVisibleSelected()}
                        aria-label="Вибрати всі видимі рядки"
                        onChange={toggleAllVisible}
                      />
                    </label>
                  </Show>
                </th>
              </Show>
              <For each={local.columns}>
                {(column) => (
                  <th
                    class="sticky top-0 z-2 border-r border-b border-border bg-surface-sunken px-3 py-2 last:border-r-0 max-md:py-0"
                    style={{ width: column.width }}
                    aria-sort={
                      sortColumnId() === column.id && column.sortable ? sortDirection() : undefined
                    }
                  >
                    <button
                      type="button"
                      class="flex w-full items-center justify-between gap-2 max-md:min-h-(--size-touch) max-md:min-w-(--size-touch) text-micro font-semibold text-text-subtle uppercase focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:cursor-default"
                      disabled={!canSort(column)}
                      onClick={() => sortBy(column)}
                    >
                      <span>{column.header}</span>
                      <Show when={sortColumnId() === column.id}>
                        {sortDirection() === 'ascending' ? (
                          <IconChevronUp size={14} strokeWidth={1.5} aria-hidden="true" />
                        ) : (
                          <IconChevronDown size={14} strokeWidth={1.5} aria-hidden="true" />
                        )}
                      </Show>
                    </button>
                  </th>
                )}
              </For>
            </tr>
          </thead>
          <tbody>
            <Show
              when={visibleRows().length > 0}
              fallback={
                <tr aria-rowindex={2}>
                  <td
                    class="px-4 py-8 text-center text-body text-text-subtle"
                    colSpan={local.columns.length + (selectionEnabled() ? 1 : 0)}
                  >
                    Записів за цим запитом немає.
                  </td>
                </tr>
              }
            >
              <For each={virtualRows().map((row) => row.key)}>
                {(key) => {
                  const entry = () => renderEntries().find((item) => item.key === key)
                  const spacerBefore = () => {
                    const index = virtualRows().findIndex((row) => row.key === key)
                    const previousEnd = virtualRows()[index - 1]?.end ?? 0
                    return Math.max(0, (virtualRows()[index]?.start ?? 0) - previousEnd)
                  }
                  const groupEntry = () => {
                    const item = entry()
                    return item?.kind === 'group' ? item : undefined
                  }
                  const rowEntry = () => {
                    const item = entry()
                    return item?.kind === 'row' ? item : undefined
                  }
                  return (
                    <>
                      <Show when={spacerBefore() > 0}>
                        <tr aria-hidden="true" tabIndex={-1}>
                          <td
                            style={{ height: `${spacerBefore()}px`, padding: 0, border: 0 }}
                            colSpan={local.columns.length + (selectionEnabled() ? 1 : 0)}
                          />
                        </tr>
                      </Show>
                      <Show
                        when={rowEntry()}
                        fallback={
                          <tr
                            data-index={(entry()?.rowIndex ?? 2) - 2}
                            aria-rowindex={entry()?.rowIndex}
                            ref={(element) => {
                              onMount(() => rowVirtualizer.measureElement(element))
                            }}
                          >
                            <td
                              class="sticky left-0 border-b border-border bg-surface-muted px-3 py-1.5"
                              colSpan={local.columns.length + (selectionEnabled() ? 1 : 0)}
                            >
                              <span class="flex items-center gap-2 text-micro font-semibold text-text-subtle uppercase">
                                {groupEntry()?.label || '—'}
                                <span class="font-mono text-text-subtle tabular-nums normal-case">
                                  {groupEntry()?.count ?? 0}
                                </span>
                              </span>
                            </td>
                          </tr>
                        }
                      >
                        {(currentRowEntry) => {
                          const row = () => currentRowEntry().row
                          const rowId = () => local.getRowId(row())
                          return (
                            <tr
                              data-index={(entry()?.rowIndex ?? 2) - 2}
                              aria-rowindex={entry()?.rowIndex}
                              ref={(element) => {
                                onMount(() => rowVirtualizer.measureElement(element))
                              }}
                              class="group transition-colors duration-(--duration-press) hover:bg-surface-muted data-[selected=true]:bg-accent-soft/45 data-[target=true]:bg-accent-soft data-[target=true]:shadow-[inset_3px_0_0_var(--color-accent)] data-[interactive=true]:cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-accent"
                              data-selected={selectedSet().has(rowId())}
                              data-target={local.targetRowId === rowId()}
                              aria-current={local.targetRowId === rowId() ? 'true' : undefined}
                              data-interactive={Boolean(local.onRowActivate)}
                              tabIndex={local.onRowActivate ? 0 : undefined}
                              onFocusIn={() => setFocusedRowId(rowId())}
                              onClick={(event) => {
                                if (isInteractiveRowTarget(event.target)) return
                                local.onRowActivate?.(row())
                              }}
                              onKeyDown={(event) => {
                                if (!local.onRowActivate) return
                                if (event.target !== event.currentTarget) return
                                if (event.key === 'Enter' || event.key === ' ') {
                                  event.preventDefault()
                                  local.onRowActivate(row())
                                }
                              }}
                            >
                              <Show when={selectionEnabled()}>
                                <td class="sticky left-0 z-1 border-r border-b border-border/70 bg-inherit px-2 py-1.5 max-md:p-0">
                                  <label class="grid place-items-center max-md:size-(--size-touch)">
                                    <input
                                      type={selectionMode() === 'single' ? 'radio' : 'checkbox'}
                                      name={
                                        selectionMode() === 'single'
                                          ? `${local['aria-label']}-selection`
                                          : undefined
                                      }
                                      class="size-4 accent-accent"
                                      checked={selectedSet().has(rowId())}
                                      aria-label={`Вибрати рядок ${rowId()}`}
                                      onClick={(event) => event.stopPropagation()}
                                      onChange={() => toggleRow(rowId())}
                                    />
                                  </label>
                                </td>
                              </Show>
                              <For each={local.columns}>
                                {(column) => (
                                  <td class="border-r border-b border-border/70 px-2 py-1.5 align-middle last:border-r-0">
                                    {column.cell(row())}
                                  </td>
                                )}
                              </For>
                            </tr>
                          )
                        }}
                      </Show>
                    </>
                  )
                }}
              </For>
              <Show when={bottomSpacer() > 0}>
                <tr aria-hidden="true" tabIndex={-1}>
                  <td
                    style={{ height: `${bottomSpacer()}px`, padding: 0, border: 0 }}
                    colSpan={local.columns.length + (selectionEnabled() ? 1 : 0)}
                  />
                </tr>
              </Show>
            </Show>
          </tbody>
        </table>
      </div>

      <footer
        ref={footerElement}
        class="flex flex-wrap items-center justify-between gap-3 border-t border-border bg-surface-muted px-3 py-2 text-micro text-text-subtle"
      >
        <span>{formatRecordCount(local.totalRows ?? visibleRows().length)}</span>
        <Show when={selectedIds().length > 0}>
          <span>Вибрано: {selectedIds().length}</span>
        </Show>
        <Show when={local.isLoadingMore}>
          <span class="ml-auto">Завантажуємо ще…</span>
        </Show>
      </footer>
    </section>
  )
}
